import { useState, useEffect, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import toast from 'react-hot-toast';
import { format, addDays, compareAsc } from "date-fns";
import ExcelJS from "exceljs";
import { saveAs } from 'file-saver';
import { 
    useTheme, 
    useMediaQuery, 
    Box, 
    Typography, 
    CircularProgress,  
    Tooltip, 
    IconButton,  
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DownloadIcon from '@mui/icons-material/Download';
import { useDateRangeContext } from 'contexts/DateRangeContext';
import useAuthCheck from 'hooks/useAuthCheck';
import { LAYOUT_HEIGHT } from 'state/constants';
import api from 'state/axios';
import { formatNumber } from 'state/helpers';
import { logClientAction } from 'state/logger';
import { buildPermissionMap, hasRight } from 'state/checkPermission';
import Header from 'components/Header';
import FlexBetween from 'components/FlexBetween';
import FlexCenter from 'components/FlexCenter';
import StyledButton from 'components/StyledButton';
import FilterButton from 'components/FilterButton';
import DataGridComponent from 'components/DataGridComponent';
import CardList from 'components/CardList';
import InventoryDialog from 'scenes/inflightSales/InventoryDialog';
import InvoiceFilterDialog from 'scenes/inflightSales/InvoiceFilterDialog';


const Inventory = () => {
    useAuthCheck();
    const user = useSelector((state) => state.user); 
    const timeMode = useSelector((state) => state.timemode);  
    const crewRanks = useSelector((state) => state.crewRanks);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const permissionMap = useMemo(() => buildPermissionMap(user?.permissions), [user?.permissions]);
    // const canCreate = hasRight(permissionMap, "Inventory", "create", false);
    const canUpdate = hasRight(permissionMap, "Inventory", "update", false);

    const { startDate, endDate } = useDateRangeContext();
    
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);    

    const [isFilterDialogOpen, setIsFilterDialogOpen] = useState(false);
    const [filters, setFilters] = useState({ isInventoryUpdated: 'ALL', hasDiscrepancy: 'ALL' });
    const defaultFilters = {
        isInventoryUpdated: 'ALL',
        hasDiscrepancy: 'ALL',
    };

    const [openInventoryDialog, setOpenInventoryDialog] = useState(false);
    const [selectedInvoice, setSelectedInvoice] = useState({
        invoiceId: '',
        flights: [],
        inventoryProducts: []
    });    
    const [invoices, setInvoices] = useState([]);

        
    const handleInvoices = useCallback(async () => {
        if (!startDate || !endDate) return [];
        
        try {           
            setError(null);
            setLoading(true);

            const url = '/inflightsales/invoices';
            const payload = { 
                startDate: addDays(startDate, -1), 
                endDate: addDays(endDate, 1),
            };
            
            const res = await api.post(url, payload);

            if (res.data) {        
                const filteredInventory = res.data.filter(invoice => {
                    const flightDateToCompare = 
                        timeMode === 'UTC' 
                            ? invoice.flightDate 
                            : invoice.flightDateLocalBase;
                    
                    return (
                        compareAsc(flightDateToCompare, startDate) >= 0 &&
                        compareAsc(flightDateToCompare, endDate) <= 0
                    );                    
                });
                
                setInvoices(filteredInventory);
            };
        } catch (error) {
            setError(error);
        } finally {
            setLoading(false);
        }
    }, [timeMode, startDate, endDate]); 

    useEffect(() => {
        handleInvoices();      
    }, [handleInvoices]);

    const filteredInvoices = useMemo(() => {
        if (!Array.isArray(invoices)) {
            return []; 
        }
        
        if (!filters) return invoices; 
    
        return invoices.filter((invoice) => {
            return Object.entries(filters).every(([key, value]) => {
                if (!value) return true; // If no value, don't filter on this key
                
                if (key === 'flightDate') {
                    const formattedRouteDate = invoice[key] ? format(new Date(invoice[key]), 'yyyy-MM-dd') : null;
                    const formattedFilterDate = value ? format(new Date(value), 'yyyy-MM-dd') : null;
                    return formattedRouteDate === formattedFilterDate;
                }
                
                if (key === 'crewRoute'  || key === 'flightNo') {
                    return invoice[key]?.toLowerCase().includes(value.toLowerCase());
                }
                
                if (key === 'isInventoryUpdated') {
                    if (value === 'ALL') return true;
                    if (value === 'UPDATED') return invoice[key] === true;
                    if (value === 'NOT_UPDATED') return invoice[key] === false;
                }

                if (key === 'hasDiscrepancy') {
                    if (value === 'ALL') return true;
                    const products = invoice.products || [];
                    const inventoryProducts = invoice.inventoryProducts || [];
                    const names = new Set([
                        ...products.map(p => p.productName),
                        ...inventoryProducts.map(p => p.productName),
                    ]);
                    const discrepant = [...names].some(name => {
                        const sell = products.find(p => p.productName === name);
                        const consume = inventoryProducts.find(p => p.productName === name);
                        return (sell?.preorderQuantity || 0) !== (consume?.preorderConsume || 0)
                            || (sell?.directQuantity   || 0) !== (consume?.directConsume   || 0);
                    });
                    if (value === 'HAS_DISCREPANCY') return discrepant;
                    if (value === 'NO_DISCREPANCY')  return !discrepant;
                }

                // For other fields, continue normal comparison
                return invoice[key] === value;
            });
        });
    }, [invoices, filters]);

    const handleFilterOpen = () => setIsFilterDialogOpen(true);
    const handleFilterClose = () => setIsFilterDialogOpen(false);
    
    const handleFilterApply = (filterValues) => {
        setFilters(filterValues); 
    };

    const handleCloseInventoryDialog = () => {
        setSelectedInvoice({ 
            invoiceId: '', 
            crewCode: '',
            crewName: '',
            flights: [], 
            products: [], 
            inventoryProducts: [] 
        });
        setOpenInventoryDialog(false);
    };
   
    const handleViewClick = (invoice) => {
        setSelectedInvoice({
            invoiceId: invoice._id,
            crewCode: invoice.crewCode,
            crewName: invoice.crewName,
            flights: invoice.selectedFlights,
            products: invoice.products,
            inventoryProducts: invoice.inventoryProducts.map(prod => ({ ...prod, purchasePrice: prod.purchasePrice }))
        });
        setOpenInventoryDialog(true);
    };
    
    const handleInvoiceSaved = (flights) => {
        // setInvoicedFlights(prev => [...prev, ...flights]);
        handleInvoices();
    };
    
    const handleExcelJSDownload = () => {
        const exportToExcel = async (data, fileName = "Inventory.xlsx") => {
            try {
                const workbook = new ExcelJS.Workbook();
                const worksheet = workbook.addWorksheet("Inventory");
    
                // 🔹 Define Fixed Columns (aligned with DataGrid)
                const fixedColumns = [
                    "No",
                    "InputId",
                    "InputBy",
                    // "CreatedAt",
                    // "UpdatedAt",
                    "RouteDate",
                    "FlightNo",
                    "CrewRoute",
                    "FlightList",
                    "CrewMembers",
                    "PreorderSell",
                    "DirectSell",
                    "PreorderConsume",
                    "DirectConsume",
                    "SupplierPayable",
                    "Preorder Sell vs Consume",
                    "Direct Sell vs Consume",
                    "IsUpdated"
                ];
    
                // 🔹 Combine All Columns (no categoryColumns now)
                const allColumns = [...fixedColumns];
    
                // 🔹 Calculate Last Column Letter for Dynamic Merging
                const getExcelColumnLetter = (colIndex) => {
                    let letter = "";
                    while (colIndex >= 0) {
                        letter = String.fromCharCode((colIndex % 26) + 65) + letter;
                        colIndex = Math.floor(colIndex / 26) - 1;
                    }
                    return letter;
                };
    
                const totalColumns = allColumns.length;
                const lastColumnLetter = getExcelColumnLetter(totalColumns - 1);
    
                // 🔹 Insert Metadata Rows
                worksheet.addRow(["Inventory"]);
                worksheet.mergeCells(`A1:${lastColumnLetter}1`);
                worksheet.getCell("A1").alignment = { horizontal: "center", vertical: "middle" };
                worksheet.getCell("A1").font = { size: 16, bold: true };
    
                const fullName = user?.firstName && user?.lastName ? `${user.lastName} ${user.firstName}` : "Unknown";
                const createdDate = `Reported by: ${fullName}, at: ${new Date().toLocaleString()}`;
                worksheet.addRow([createdDate]);
                worksheet.mergeCells(`A2:${lastColumnLetter}2`);
                worksheet.getCell("A2").alignment = { horizontal: "center", vertical: "middle" };
                worksheet.getCell("A2").font = { italic: true };
    
                worksheet.addRow([`Time mode: ${timeMode}`]);
                worksheet.mergeCells(`A3:${lastColumnLetter}3`);
                worksheet.getCell("A3").alignment = { horizontal: "center", vertical: "middle" };
                worksheet.getCell("A3").font = { italic: true };
    
                worksheet.addRow([]); // Empty Row Before Headers
    
                // 🔹 Add Column Headers
                const headerRow = worksheet.addRow(allColumns);
                headerRow.eachCell((cell) => {
                    cell.font = { bold: true };
                    cell.fill = {
                        type: "pattern",
                        pattern: "solid",
                        fgColor: { argb: "FFFFCC" },
                    };
                    cell.alignment = { horizontal: "center", vertical: "middle", wrapText: true };
                });
    
                // 🔹 Transform Data Dynamically (without categorySummary)
                const transformedInvoices = data.map((invoice, index) => {
                    const crewMembers = invoice.crewMembers || [];
                    const sortedFlights = [...(invoice.selectedFlights || [])].sort((a, b) => {
                        const dateComparison = new Date(a.flightDate) - new Date(b.flightDate);
                        return dateComparison !== 0 ? dateComparison : a.etd.localeCompare(b.etd);
                    });
    
                    // FlightList
                    const flightList = sortedFlights.map(flight => 
                        `${flight.flightNo} ${timeMode === 'UTC' ? format(flight.flightDate, "ddMMMyy") : format(flight.flightDateLocalBase, "ddMMMyy")} ${timeMode === 'UTC' ? flight.etd : flight.etdLocalBase} ${flight.dep}-${flight.arr} ${timeMode === 'UTC' ? flight.eta : flight.etaLocalBase}`
                    ).join("\n");
    
                    // CrewMembers
                    const sortedCrewMembers = crewMembers
                        .map(member => {
                            const rank = crewRanks.find(rank => rank.rankCode === member.post) || {};
                            return { ...member, rankPriority: rank.rankPriority || Infinity };
                        })
                        .sort((a, b) => a.rankPriority - b.rankPriority);
                    const crewMembersList = sortedCrewMembers.map(cm => `${cm.crewCode} ${cm.crewName} (${cm.post} | ${cm.base})`).join("\n");
    
                    // PreorderSell
                    const preorderSellProducts = invoice.products?.filter(product => product.preorderQuantity > 0) || [];
                    const preorderSell = preorderSellProducts.map(p => `${p.productName}: ${p.preorderQuantity || 0}`).join("\n") || "";
    
                    // DirectSell
                    const directSellProducts = invoice.products?.filter(product => product.directQuantity > 0) || [];
                    const directSell = directSellProducts.map(p => `${p.productName}: ${p.directQuantity || 0}`).join("\n") || "";
    
                    // PreorderConsume
                    const preorderConsumeProducts = invoice.inventoryProducts?.filter(product => product.preorderConsume > 0) || [];
                    const preorderConsume = preorderConsumeProducts.map(p => `${p.productName}: ${p.preorderConsume || 0}`).join("\n") || "";
    
                    // DirectConsume
                    const directConsumeProducts = invoice.inventoryProducts?.filter(product => product.directConsume > 0) || [];
                    const directConsume = directConsumeProducts.map(p => `${p.productName}: ${p.directConsume || 0}`).join("\n") || "";
    
                    const filteredProducts = invoice.inventoryProducts
                        ?.filter(prod => (prod.preorderConsume || 0) > 0 || (prod.directConsume || 0) > 0);
                    
                    const supplierPayable = filteredProducts.reduce((acc, prod) => {
                        return acc + ((prod.preorderConsume || 0) * (prod.purchasePrice || 0)) +
                            ((prod.directConsume || 0) * (prod.purchasePrice || 0));
                    }, 0);

                    // Preorder Comparison
                    const preorderProductNames = Array.from(new Set([
                        ...preorderSellProducts.map(p => p.productName),
                        ...preorderConsumeProducts.map(p => p.productName)
                    ]));
                    const preorderComparison = preorderProductNames.map(productName => {
                        const sellProduct = preorderSellProducts.find(p => p.productName === productName) || {};
                        const consumeProduct = preorderConsumeProducts.find(p => p.productName === productName) || {};
                        const sellQty = sellProduct.preorderQuantity || 0;
                        const consumeQty = consumeProduct.preorderConsume || 0;
                        const difference = sellQty - consumeQty;
                        return difference !== 0 ? `${productName}: ${difference > 0 ? '+' : ''}${difference}` : null;
                    }).filter(item => item !== null).join("\n") || "";
    
                    // Direct Comparison
                    const directProductNames = Array.from(new Set([
                        ...directSellProducts.map(p => p.productName),
                        ...directConsumeProducts.map(p => p.productName)
                    ]));
                    const directComparison = directProductNames.map(productName => {
                        const sellProduct = directSellProducts.find(p => p.productName === productName) || {};
                        const consumeProduct = directConsumeProducts.find(p => p.productName === productName) || {};
                        const sellQty = sellProduct.directQuantity || 0;
                        const consumeQty = consumeProduct.directConsume || 0;
                        const difference = sellQty - consumeQty;
                        return difference !== 0 ? `${productName}: ${difference > 0 ? '+' : ''}${difference}` : null;
                    }).filter(item => item !== null).join("\n") || "";
    
                    // isUpdated
                    const filteredInventoryProducts = invoice.inventoryProducts?.filter(product => product.directStockIn > 0 || product.directPreviousReceipt > 0) || [];
                    const isUpdated = filteredInventoryProducts.length > 0 ? "True" : "False";
    
                    return [
                        index + 1,
                        invoice.crewCode,
                        invoice.crewName,
                        // invoice.createdAt ? format(invoice.createdAt, "HH:mm:ss dd/MM/yy") : "",
                        // invoice.updatedAt ? format(invoice.updatedAt, "HH:mm:ss dd/MM/yy") : "",
                        timeMode === "UTC" ? (invoice.flightDate ? format(invoice.flightDate, "dd/MM/yy") : "") : (invoice.flightDateLocalBase ? format(invoice.flightDateLocalBase, "dd/MM/yy") : ""),
                        invoice.flightNo,
                        invoice.crewRoute,
                        flightList,
                        crewMembersList,
                        preorderSell,
                        directSell,
                        preorderConsume,
                        directConsume,
                        supplierPayable,
                        preorderComparison,
                        directComparison,
                        isUpdated
                    ];
                });
    
                // 🔹 Add Data Rows
                transformedInvoices.forEach(row => worksheet.addRow(row));
    
                // 🔹 Apply Styling
                worksheet.eachRow((row) => {
                    row.eachCell((cell) => {
                        cell.alignment = { vertical: "middle", horizontal: "center", wrapText: true };
                        cell.border = {
                            top: { style: "thin" },
                            left: { style: "thin" },
                            bottom: { style: "thin" },
                            right: { style: "thin" },
                        };
                    });
                });
    
                // 🔹 Generate and Save File
                const buffer = await workbook.xlsx.writeBuffer();
                saveAs(new Blob([buffer]), fileName);
            } catch (error) {
                toast.error(error?.message || "Failed to export to Excel!");
            }
        };
    
        if (!invoices || invoices.length === 0) {
            toast.error("No data available to export!");
            return;
        }
    
        // Add Timestamp to Filename
        const timestamp = new Date().toISOString().replace(/[-T:.Z]/g, "").slice(0, 14);
        const fileName = `Inventory_${timestamp}.xlsx`;
    
        exportToExcel(invoices, fileName);
    };

    // const handleExcelJSDownload = () => {
    //     if (!invoices || invoices.length === 0) {
    //         toast.error("No data available to export!");
    //         return;
    //     }
    
    //     const timestamp = new Date().toISOString().replace(/[-T:.Z]/g, "").slice(0, 14);
    //     const fileName = `Inventory_${timestamp}.xlsx`;

    //     exportToExcel(invoices, fileName);
    // };

    const columns = useMemo(() => {
        const getColumnWidth = (baseWidth, mobileReduction = 0) =>
            isMobile ? baseWidth - mobileReduction : baseWidth;
      
        // Reusable render function for product-related columns
        const renderProductCell = (products, quantityField, width) => {
            const filteredProducts = products?.filter((product) => (product[quantityField] || 0) !== 0) || [];
            return (
                <Box sx={{ height: '100%', alignItems: 'flex-start', overflowY: 'auto', overflowX: 'hidden' }}>
                    {filteredProducts.map((product, index) => (
                        <Box key={index} display="flex" alignItems="center" gap={1}>
                            <Typography variant="body2" sx={{ textAlign: 'right', whiteSpace: 'nowrap', width: 12 }}>
                                {`${index + 1}. `}
                            </Typography>
                            <Typography
                                variant="body2"
                                sx={{ textAlign: 'left', whiteSpace: 'nowrap', width: getColumnWidth(width - 32, 50) }}
                            >
                                {product.productName}
                            </Typography>
                            <Typography variant="body2" sx={{ textAlign: 'right', whiteSpace: 'nowrap', width: 20 }}>
                                {product[quantityField] || 0}
                            </Typography>
                        </Box>
                    ))}
                </Box>
            );
        };
      
        // Reusable render function for comparison columns
        const renderComparisonCell = (sellProducts, consumeProducts, sellField, consumeField, width) => {
            const productNames = Array.from(
                new Set([...sellProducts.map((p) => p.productName), ...consumeProducts.map((p) => p.productName)])
            );
            const comparison = productNames
                .map((productName) => {
                    const sellProduct = sellProducts.find((p) => p.productName === productName) || {};
                    const consumeProduct = consumeProducts.find((p) => p.productName === productName) || {};
                    const sellQty = sellProduct[sellField] || 0;
                    const consumeQty = consumeProduct[consumeField] || 0;
                    const difference = sellQty - consumeQty;
                    return { productName, difference };
                })
                .filter((item) => item.difference !== 0);
      
            return (
                <Box sx={{ height: '100%', alignItems: 'flex-start', overflowY: 'auto', overflowX: 'hidden' }}>
                    {comparison.length > 0 ? (
                        comparison.map((item, index) => (
                        <Box key={index} display="flex" alignItems="center" gap={1}>
                            <Typography variant="body2" sx={{ textAlign: 'right', whiteSpace: 'nowrap', width: 12 }}>
                                {`${index + 1}.`}
                            </Typography>
                            <Typography
                                variant="body2"
                                sx={{ textAlign: 'left', whiteSpace: 'nowrap', width: getColumnWidth(width - 32, 50) }}
                            >
                                {item.productName}
                            </Typography>
                            <Typography
                                variant="body2"
                                sx={{
                                    textAlign: 'right',
                                    whiteSpace: 'nowrap',
                                    width: 20,
                                    color: item.difference > 0 ? 'green' : 'red',
                                }}
                            >
                                {item.difference > 0 ? `+${item.difference}` : item.difference}
                            </Typography>
                        </Box>
                        ))
                    ) : (
                        <Typography variant="body2" sx={{ textAlign: 'center', color: 'gray' }} />
                    )}
                </Box>
            );
        };
      
        // Configuration for product-related columns
        const productColumnsConfig = [
            { field: 'preorderSell', headerName: 'PreorderSell', quantityField: 'preorderQuantity', source: 'products' },
            { field: 'directSell', headerName: 'DirectSell', quantityField: 'directQuantity', source: 'products' },
            {
                field: 'preorderStockIn',
                headerName: 'PreorderStockIn',
                quantityField: 'preorderStockIn',
                source: 'inventoryProducts',
            },
            {
                field: 'preorderStockOn',
                headerName: 'PreorderStockOn',
                quantityField: 'preorderStockOn',
                source: 'inventoryProducts',
            },
            {
                field: 'preorderConsume',
                headerName: 'PreorderConsume',
                quantityField: 'preorderConsume',
                source: 'inventoryProducts',
            },
            {
                field: 'directStockIn',
                headerName: 'DirectStockIn',
                quantityField: 'directStockIn',
                source: 'inventoryProducts',
            },
            {
                field: 'directStockOn',
                headerName: 'DirectStockOn',
                quantityField: 'directStockOn',
                source: 'inventoryProducts',
            },
            {
                field: 'directConsume',
                headerName: 'DirectConsume',
                quantityField: 'directConsume',
                source: 'inventoryProducts',
            },
        ];
      
        return [
            {
                field: 'no',
                headerName: 'No',
                headerClassName: 'theme-column--header',
                filterable: false,
                sortable: false,
                renderCell: (params) => params.api.getAllRowIds().indexOf(params.id) + 1,
                width: 50,
            },
            {
                field: 'crewCode',
                headerName: 'InputId',
                headerClassName: 'theme-column--header',
                width: getColumnWidth(60),
            },
            {
                field: 'crewName',
                headerName: 'InputBy',
                headerClassName: 'theme-column--header',
                width: getColumnWidth(280, 50),
            },
            {
                field: 'createdAt',
                headerName: 'CreatedAt',
                headerClassName: 'theme-column--header',
                valueFormatter: (value) => (value ? format(value, 'HH:mm:ss dd/MM/yy') : ''),
                width: getColumnWidth(130),
            },
            {
                field: 'updatedAt',
                headerName: 'UpdatedAt',
                headerClassName: 'theme-column--header',
                pinned: 'left',
                valueFormatter: (value) => (value ? format(value, 'HH:mm:ss dd/MM/yy') : ''),
                width: getColumnWidth(130),
            },
            {
                field: timeMode === 'UTC' ? 'flightDate' : 'flightDateLocalBase',
                headerName: 'RouteDate',
                headerClassName: 'theme-column--header',
                pinned: 'left',
                valueFormatter: (value) => (value ? format(value, 'dd/MM/yy') : ''),
                width: getColumnWidth(100),
            },
            {
                field: 'flightNo',
                headerName: 'FlightNo',
                headerClassName: 'theme-column--header',
                cellClassName: 'name-column--cell',
                width: getColumnWidth(150),
            },
            {
                field: 'crewRoute',
                headerName: 'CrewRoute',
                headerClassName: 'theme-column--header',
                width: getColumnWidth(165),
            },
            {
                field: 'selectedFlights',
                headerName: 'FlightList',
                width: getColumnWidth(330, 10),
                renderCell: (params) => {
                const sortedFlights = [...(params.row.selectedFlights || [])].sort((a, b) => {
                    const dateComparison = new Date(a.flightDate) - new Date(b.flightDate);
                    return dateComparison !== 0 ? dateComparison : a.etd.localeCompare(b.etd);
                });
                return (
                    <Box sx={{ height: '100%', alignItems: 'flex-start', overflowY: 'auto', overflowX: 'hidden' }}>
                        {sortedFlights.map((flight, index) => (
                            <Box key={index} display="flex" alignItems="center" gap={1}>
                                <Typography variant="body2" sx={{ textAlign: 'right', whiteSpace: 'nowrap', width: 32 }}>
                                    {flight.flightNo}
                                </Typography>                                
                                <Typography
                                    variant="body2"
                                    sx={{
                                        textAlign: 'right',
                                        width: '54px',
                                    }}
                                >
                                    {flight.flightReg || flight.acType}
                                </Typography>
                                <Typography variant="body2" sx={{ textAlign: 'center', width: 45 }}>
                                    {timeMode === 'UTC' ? format(flight.flightDate, 'ddMMMyy') : format(flight.flightDateLocalBase, 'ddMMMyy')}
                                </Typography>
                                <Typography variant="body2" sx={{ textAlign: 'center', width: 30 }}>
                                    {timeMode === 'UTC' ? flight.etd : flight.etdLocalBase}
                                </Typography>
                                <Typography variant="body2" sx={{ textAlign: 'center', width: 25 }}>
                                    {flight.dep}
                                </Typography>
                                <Typography variant="body2" sx={{ textAlign: 'center', width: 25 }}>
                                    {flight.arr}
                                </Typography>
                                <Typography variant="body2" sx={{ textAlign: 'center', width: 30 }}>
                                    {timeMode === 'UTC' ? flight.eta : flight.etaLocalBase}
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                );
                },
            },
            {
                field: 'crewMembers',
                headerName: 'CrewMembers',
                width: getColumnWidth(350, 20),
                renderCell: (params) => {
                    const sortedCrewMembers = params.row.crewMembers
                        ?.map((member) => {
                            const rank = crewRanks.find((rank) => rank.rankCode === member.post) || {};
                            return { ...member, rankPriority: rank.rankPriority || Infinity };
                        })
                        .sort((a, b) => a.rankPriority - b.rankPriority);
                    return (
                        <Box sx={{ height: '100%', alignItems: 'flex-start', overflowY: 'auto', overflowX: 'hidden' }}>
                            {sortedCrewMembers?.map((member, index) => (
                                <Box key={index} display="flex" alignItems="center" gap={1}>
                                    <Typography variant="body2" sx={{ textAlign: 'right', whiteSpace: 'nowrap', width: 30 }}>
                                        {member.crewCode}
                                    </Typography>
                                    <Typography variant="body2" sx={{ textAlign: 'left' }}>
                                        {member.crewName}
                                    </Typography>
                                    <Typography variant="body2" sx={{ textAlign: 'center' }}>
                                        ({member.post} | {member.base})
                                    </Typography>
                                </Box>
                            ))}
                        </Box>
                    );
                },
            },
            ...productColumnsConfig.map(({ field, headerName, quantityField, source }) => ({
                field,
                headerName,
                width: getColumnWidth(350, 50),
                renderCell: (params) => renderProductCell(params.row[source], quantityField, 350),
            })),
            {
                field: 'supplierPayable',
                headerName: 'SupplierPayable',
                width: getColumnWidth(150, 30),
                renderCell: (params) => {
                    const filteredProducts = params.row.inventoryProducts?.filter(
                        (prod) => (prod.preorderConsume || 0) > 0 || (prod.directConsume || 0) > 0
                    );
                    const totalPayable = Math.round(
                        filteredProducts.reduce(
                        (acc, prod) =>
                            acc +
                            (prod.preorderConsume || 0) * (prod.purchasePrice || 0) +
                            (prod.directConsume || 0) * (prod.purchasePrice || 0),
                        0
                        )
                    );
                    return (
                        <Box
                            sx={{
                                height: '100%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'flex-end',
                                overflow: 'hidden',
                                width: '100%',
                            }}
                            >
                            <Typography variant="body2" sx={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
                                {totalPayable.toLocaleString()}
                            </Typography>
                        </Box>
                    );
                },
            },
            {
                field: 'preorderAmount',
                headerName: 'PreorderRevenue',
                headerClassName: 'theme-column--header',
                width: getColumnWidth(150, 30),
                align: 'right',
                valueFormatter: (params) => (params ? params.toLocaleString() : ''),
            },
            {
                field: 'directAmount',
                headerName: 'DirectRevenue',
                headerClassName: 'theme-column--header',
                width: getColumnWidth(150, 30),
                align: 'right',
                valueFormatter: (params) => (params ? params.toLocaleString() : ''),
            },
            {
                field: 'totalAmount',
                headerName: 'TotalRevenue',
                headerClassName: 'theme-column--header',
                width: getColumnWidth(150, 30),
                align: 'right',
                valueFormatter: (params) => (params ? params.toLocaleString() : ''),
            },
            {
                field: 'preorderComparison',
                headerName: 'Preorder Sell vs Consume',
                width: getColumnWidth(350, 50),
                renderCell: (params) =>
                renderComparisonCell(
                    params.row.products.filter((p) => p.isInventory !== false) || [],
                    params.row.inventoryProducts || [],
                    'preorderQuantity',
                    'preorderConsume',
                    350
                ),
            },
            {
                field: 'directComparison',
                headerName: 'Direct Sell vs Consume',
                width: getColumnWidth(350, 50),
                renderCell: (params) =>
                renderComparisonCell(
                    params.row.products.filter((p) => p.isInventory !== false) || [],
                    params.row.inventoryProducts || [],
                    'directQuantity',
                    'directConsume',
                    350
                ),
            },
            {
                field: 'isInventoryUpdated',
                headerName: 'IsUpdated',
                width: 80,
            },
        ];
    }, [timeMode, isMobile, crewRanks]);
    
    const nonMobileHiddenColumns = {
        _id: false,      
        createdAt: false,
        updatedAt: false,     
    };
    
    const actionColumn = useMemo(() => {        
        const activeCount = [canUpdate].filter(Boolean).length;
        if (activeCount === 0) return [];
        const baseWidth = 85 - (1 - activeCount) * 20;

        return [{
            field: "action",
            headerName: "Action",
            width: baseWidth,
            filterable: false,      
            sortable: false,
            renderCell: (params) => (
                <FlexCenter
                    gap={1}
                    sx={{ width: '100%', height: '100%' }}
                >         
                    {canUpdate && (
                        <Tooltip title="Update">
                            <IconButton
                                aria-label="Update"
                                sx={{ color: "primary.dark" }}
                                onClick={() => {
                                    if (!canUpdate) return;
                                    handleViewClick(params.row);
                                }}
                            >
                                <EditIcon />
                            </IconButton>                
                        </Tooltip>
                    )}   
                </FlexCenter>
            ),
        }]
    }, [canUpdate]);

    const columnsToShow = [...columns, ...actionColumn];

        
    return (
        <Box className="main-container-box">             
            <FlexBetween>
                <Header 
                    title="Inventory" 
                    subtitle={`${loading ? 'Loading...' : 'Inventory Tracking'}`}
                /> 
                {!loading && (
                    <Box display="flex" alignItems="center" gap={1}>
                        <Tooltip title="Download Excel file" arrow>
                            <span>
                                <StyledButton 
                                    onClick={() => {
                                    if (!hasRight(permissionMap, "Inventory", "export")) return;
                                    handleExcelJSDownload();
                                    logClientAction("EXPORT_REPORT", { fileName: "Inventory", format: "Excel", startDate, endDate });
                                }} 
                            >
                                    <DownloadIcon />
                                </StyledButton>
                            </span>
                        </Tooltip>      
                        <FilterButton
                            filters={filters}
                            defaultFilters={defaultFilters}
                            onClick={handleFilterOpen}
                        />                  
                    </Box>
                )}                
            </FlexBetween>              
            {loading ? (
                <FlexCenter height={LAYOUT_HEIGHT}>
                    <CircularProgress />
                </FlexCenter>
            ) : error ? (
                <FlexCenter height={LAYOUT_HEIGHT}>
                    <Typography color="error">
                        Error: {error?.message || ''}
                    </Typography>
                </FlexCenter>
            ) : (
                <Box
                    display="grid"
                    gridTemplateColumns="repeat(12, minmax(0, 1fr))"
                    gap={2}
                >
                    <Box
                        height={LAYOUT_HEIGHT}
                        sx={{ gridColumn: "span 12" }}
                    >
                        {isMobile ? (
                            <CardList
                                rows={filteredInvoices}
                                primaryField="crewName"
                                secondaryField="crewCode"
                                isActiveField={null}
                                minWidth={95}
                                fields={[
                                    { label: "Route Date",       key: "flightDate",              render: (r) => r.flightDate ? format(new Date(r.flightDate), "dd/MM/yy") : null },
                                    { label: "Flight No",        key: "flightNo" },
                                    { label: "Crew Route",       key: "crewRoute" },
                                    { label: "Pre Revenue",      key: "preorderAmount", cols: 2, render: (r) => formatNumber(r.preorderAmount) },
                                    { label: "Dir Revenue",      key: "directAmount",   cols: 2, render: (r) => formatNumber(r.directAmount) },
                                    { label: "Total Revenue",    key: "totalAmount",             render: (r) => formatNumber(r.totalAmount) },
                                    { label: "Is Updated",       key: "isInventoryUpdated",      render: (r) => r.isInventoryUpdated ? "Yes" : "No" },
                                ]}
                                canView={false}
                                canEdit={canUpdate}
                                canDelete={false}
                                onEdit={(id) => {
                                    const item = filteredInvoices.find(inv => String(inv._id) === String(id));
                                    if (item && hasRight(permissionMap, "Inventory", "create")) {
                                        handleViewClick(item);
                                    }
                                }}
                            />
                        ) : (
                            <DataGridComponent
                                rows={filteredInvoices}
                                columns={columnsToShow}
                                loading={loading}
                                props={{
                                    getRowClassName: (params) =>
                                        params.row.isInventoryUpdated === false ? "inventory-not-updated" : "",
                                    initialState: {
                                        pagination: { paginationModel: { pageSize: 100 } },
                                        columns: { columnVisibilityModel: nonMobileHiddenColumns },
                                    },
                                }}
                                sx={{
                                    "& .MuiDataGrid-row.inventory-not-updated": {
                                        backgroundColor: `${theme.palette.green.light} !important`,  
                                    },  
                                }}
                            />
                        )}
                    </Box>                
                </Box>
            )}

            {isFilterDialogOpen &&
                <InvoiceFilterDialog
                    open={isFilterDialogOpen}
                    onClose={handleFilterClose}
                    onApply={handleFilterApply}
                    isInventoryUpdatedVisible={true}
                    currentFilters={filters}
                />
            }

            {openInventoryDialog && 
                <InventoryDialog
                    open={openInventoryDialog}
                    onClose={handleCloseInventoryDialog}
                    selectedInvoiceId={selectedInvoice.invoiceId} 
                    crewCode={selectedInvoice.crewCode}
                    crewName={selectedInvoice.crewName}
                    selectedFlights={selectedInvoice.flights}
                    selectedProducts={selectedInvoice.products}
                    selectedInventoryProducts={selectedInvoice.inventoryProducts}
                    onInvoiceSaved={handleInvoiceSaved}
                />
            }
        </Box>
    );
};

export default Inventory;
